// What this page shows when it breaks.
//
// Without a boundary a render error takes the whole tree down and leaves a
// blank white page, which on this screen is the worst failure the product
// has: somebody opened a link to find out where their parcel is, and got
// nothing — no way to tell whether the run ended, the link is wrong, or
// the page is broken.
//
// Two rules here, and both are about not making it worse.
//
// **The capability does not appear.** It is in the URL, and error text is
// where URLs leak. `safeErrorText` scrubs it in `packages/core`, where it
// is tested; this component must not route around it by rendering
// `error.stack` or `location.href` for "context".
//
// **It does not claim to know what happened.** A blank page at least does
// not lie. "Something went wrong on this page" plus a reload is the whole
// honest offering — and reload genuinely helps, because the capability is
// in the fragment and survives it.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { safeErrorText } from "@wayfind/core";

interface State {
  failed: boolean;
  detail: string | null;
}

export class Boundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false, detail: null };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, detail: safeErrorText(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is a developer's, not a reporter's — nothing leaves the
    // device here, and nothing should start to without the scrubbing
    // above coming with it.
    console.error("wayfind: render failed", safeErrorText(error), info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="shell">
        <div className="shell__inner">
          <section className="panel">
            <p className="eyebrow">Something went wrong on this page</p>
            <p style={{ margin: "0 0 0.75rem" }}>
              This is a fault in the page, not in your delivery. Nothing about it
              has been sent anywhere.
            </p>
            <button type="button" className="ghost-button" onClick={() => location.reload()}>
              Try again
            </button>
            {this.state.detail ? (
              <p className="field__hint" style={{ marginTop: "0.75rem" }}>
                {this.state.detail}
              </p>
            ) : null}
          </section>
          <footer className="footer">
            <p>Your link is still in the address bar. Reloading keeps it.</p>
          </footer>
        </div>
      </div>
    );
  }
}
